import { FunctionalErrorResponse, RestError } from '@axa-fr-axion/restful';
import { QueryClientConfig, QueryClientProvider } from '@tanstack/react-query';
import { act, render, screen, waitForElementToBeRemoved, within } from '@testing-library/react';
import App from 'App';
import { JourneyData } from 'App.interface';
import { NavigationPath } from 'interfaces';
import { PropsWithChildren } from 'react';
import { getQueryClient } from 'utils/query-client';

/**
 * Misc
 */

export const isTestMode = () => process.env.NODE_ENV === 'test';

// In Jest test, we don't want to retry the query
export const getReactQueryRetryFnc = (
    fnc: (failureCount: number, error: RestError<FunctionalErrorResponse>) => boolean
) => {
    if (isTestMode()) {
        return false;
    }
    return fnc;
};

export const getQueryClientOptions = (options?: QueryClientConfig) => {
    const queryClient = getQueryClient(options);

    const wrapper = ({ children }: PropsWithChildren): JSX.Element => (
        <QueryClientProvider client={ queryClient }>
            { children }
        </QueryClientProvider>
    );

    const defaultOptions = {
        wrapper,
    };

    return { defaultOptions, queryClient };
};

export * from '@testing-library/react';

/**
 * UI
 */

export type InputStatus = 'valide' | 'invalide';

export const checkInputValidity = async (name: string, value: string, inputStatus: InputStatus, maxLength: number) => {
    const input: HTMLInputElement = screen.getByRole('textbox', {
        name,
    });

    expect(input).toHaveValue(value.slice(0, maxLength));

    if (inputStatus === 'invalide') {
        expect(input).toBeInvalid();
    } else {
        expect(input).not.toBeInvalid();
    }
};

export const clickOnButton = async (name: string) => {
    await act(async () => {
        const element = screen.getByRole('button', { name }) as HTMLButtonElement;
        element.click();
    });
};

export const doNothing = () => {};

export const expectHeading = async (title: string): Promise<void> => {
    const main: HTMLElement = screen.getByRole('region', { name: 'Parcours Pro/Ent' });

    await within(main).findAllByText(RegExp(title, 'i'));
    expect(within(main).getByRole('heading', { name: title })).toBeInTheDocument();
};

export const renderApp = (
    path?: NavigationPath,
    data?: Partial<JourneyData>,
): void => void render(
    <App path={ path } data={ data } />
);

export const getInput = (name: string) => {
    const input: HTMLInputElement = screen.getByRole('textbox', {
        name,
    });
    return input;
};

export const wait = async (time: number) => new Promise(resolve => setTimeout(resolve, time));

export const waitForLoaderToBeRemoved = async () => {
    await wait(200);

    if (Boolean(screen.queryByText(/Veuillez patienter/i))) {
        await waitForElementToBeRemoved(() => screen.queryByText(/Veuillez patienter/i));
    }
};

export const waitForStepTransition = async () => {
    if (Boolean(screen.queryByAltText('Illustration du chargement'))) {
        await waitForElementToBeRemoved(screen.queryByAltText('Illustration du chargement'));
    }
};

export const getInputByName = (name: string): HTMLInputElement => {
    return screen.getByRole('textbox', {
        name,
    });
};
